import * as zod from 'zod';

import { CNCMonitorMachineStepDatabase } from '@sb/integrations/implementations/CNCMachine/step-wizard/CNCMonitorMachine/CNCMonitorMachineStepDatabase';
import { CNCOperateAutodoorStepDatabase } from '@sb/integrations/implementations/CNCMachine/step-wizard/CNCOperateAutodoor/CNCOperateAutodoorStepDatabase';
import { CNCOperateWorkholdingStepDatabase } from '@sb/integrations/implementations/CNCMachine/step-wizard/CNCOperateWorkholding/CNCOperateWorkholdingStepDatabase';
import { CNCRunProgramStepDatabase } from '@sb/integrations/implementations/CNCMachine/step-wizard/CNCRunProgram/CNCRunProgramStepDatabase';
import { MoveVerticalLiftStepDatabase } from '@sb/integrations/implementations/dynamic-base/step-wizard/MoveVerticalLiftStepDatabase';
import { ActuateGripperStepDatabase } from '@sb/integrations/implementations/gripper-general/step-wizard/ActuateGripperStepDatabase';
import { DisableGripperStepDatabase } from '@sb/integrations/implementations/gripper-general/step-wizard/DisableGripperStepDatabase';
import { EnableGripperStepDatabase } from '@sb/integrations/implementations/gripper-general/step-wizard/EnableGripperStepDatabase';
import { HaasControlRegionStepDatabase } from '@sb/integrations/implementations/HaasMill/frontend/HaasControlRegionStepDatabase';
import { HaasRunProgramStepDatabase } from '@sb/integrations/implementations/HaasMill/frontend/HaasRunProgramStepDatabase';
import { ActuateScrewdriverStepDatabase } from '@sb/integrations/implementations/onRobot/OnRobotScrewdriver/step-wizard/ActuateScrewdriverStepDatabase';
import { ActuateVacuumStepDatabase } from '@sb/integrations/implementations/onRobot/OnRobotVGP20/step-wizard/ActuateVacuumStepDatabase';
import { WeldStepDatabase } from '@sb/integrations/implementations/WeldMachine/shared/step-wizard/Weld/WeldStepDatabase';

import { AddOffsetStep } from './addOffset';
import { AnticipatePayloadStep } from './anticipatePayload';
import { AntigravityStep } from './antigravity';
import { ArcStep } from './arc';
import { CaptureStep } from './capture';
import { ClassifyStep } from './classify';
import { CodeBlockStep } from './codeBlock';
import { CreateRecordingStep } from './createRecording';
import { ElseStep } from './else';
import { ElseIfStep } from './elseIf';
import { IfStep } from './if';
import { LocateStep } from './locate';
import { LoopStep } from './loop';
import { LoopControlStep } from './loopControl';
import { MoveArmToTargetStep } from './moveArmTo';
import { MoveArmToTargetStepV2 } from './moveArmToV2';
import { NetworkRequestStep } from './networkRequest';
import { OperateEquipmentStep } from './operateEquipment';
import { PressButtonStep } from './pressButton';
import { PushModeStep } from './pushMode';
import { RunInBackgroundStep } from './runInBackground';
import { RunSkillStep } from './runSkill';
import { SetEnvironmentVariableStep } from './setEnvironmentVariable';
import { SetIOStep } from './setIO';
import { TeleopStep } from './teleop';
import { TriggerFaultStep } from './triggerFault';
import { UpdateVisualizationStep } from './updateVisualization';
import { WaitStep } from './wait';
import { WaitForConfirmationStep } from './waitForConfirmation';
import { WaypointStep } from './waypoint';

/**
 * Makes sure each 'stepKind' has the right configuration.
 * For example, "If" steps receive different arguments than
 * "Loop" steps.
 */
export const StepConfigurationByKind = zod.discriminatedUnion('stepKind', [
  zod.object({
    args: ActuateScrewdriverStepDatabase.Arguments.optional(),
    stepKind: zod.literal('ActuateScrewdriver'),
  }),
  zod.object({
    args: ActuateGripperStepDatabase.Arguments.optional(),
    stepKind: zod.literal('ActuateGripper'),
  }),
  zod.object({
    args: ActuateVacuumStepDatabase.Arguments.optional(),
    stepKind: zod.literal('ActuateVacuum'),
  }),
  zod.object({
    args: AnticipatePayloadStep.Arguments.optional(),
    stepKind: zod.literal('AnticipatePayload'),
  }),
  zod.object({
    args: AntigravityStep.Arguments.optional(),
    stepKind: zod.literal('Antigravity'),
  }),
  zod.object({
    args: AddOffsetStep.Arguments.optional(),
    stepKind: zod.literal('AddOffset'),
  }),
  zod.object({
    args: ArcStep.Arguments.optional(),
    stepKind: zod.literal('Arc'),
  }),
  zod.object({
    args: CaptureStep.Arguments.optional(),
    stepKind: zod.literal('Capture'),
  }),
  zod.object({
    args: ClassifyStep.Arguments.optional(),
    stepKind: zod.literal('Classify'),
  }),
  zod.object({
    args: CodeBlockStep.Arguments.optional(),
    stepKind: zod.literal('CodeBlock'),
  }),
  zod.object({
    args: CreateRecordingStep.Arguments.optional(),
    stepKind: zod.literal('CreateRecording'),
  }),
  zod.object({
    args: LocateStep.Arguments.optional(),
    stepKind: zod.literal('Locate'),
  }),
  zod.object({
    args: HaasControlRegionStepDatabase.Arguments.optional(),
    stepKind: zod.literal('HaasControlRegion'),
  }),
  zod.object({
    args: HaasRunProgramStepDatabase.Arguments.optional(),
    stepKind: zod.literal('HaasRunProgram'),
  }),
  zod.object({
    args: ElseStep.Arguments.optional(),
    stepKind: zod.literal('Else'),
  }),
  zod.object({
    args: ElseIfStep.Arguments.optional(),
    stepKind: zod.literal('ElseIf'),
  }),
  zod.object({
    args: IfStep.Arguments.optional(),
    stepKind: zod.literal('If'),
  }),
  zod.object({
    args: LoopStep.Arguments.optional(),
    stepKind: zod.literal('Loop'),
  }),
  zod.object({
    args: LoopControlStep.Arguments.optional(),
    stepKind: zod.literal('LoopControl'),
  }),
  zod.object({
    args: MoveArmToTargetStep.Arguments.optional(),
    stepKind: zod.literal('MoveArmTo'),
  }),
  zod.object({
    args: MoveArmToTargetStepV2.Arguments.optional(),
    stepKind: zod.literal('MoveArmToV2'),
  }),
  zod.object({
    args: NetworkRequestStep.Arguments.optional(),
    stepKind: zod.literal('NetworkRequest'),
  }),
  zod.object({
    args: OperateEquipmentStep.Arguments.optional(),
    stepKind: zod.literal('OperateEquipment'),
  }),
  zod.object({
    args: PressButtonStep.Arguments.optional(),
    stepKind: zod.literal('PressButton'),
  }),
  zod.object({
    args: PushModeStep.Arguments.optional(),
    stepKind: zod.literal('PushMode'),
  }),
  zod.object({
    args: RunInBackgroundStep.Arguments.optional(),
    stepKind: zod.literal('RunInBackground'),
  }),
  zod.object({
    args: RunSkillStep.Arguments.optional(),
    stepKind: zod.literal('RunSkill'),
  }),
  zod.object({
    args: SetEnvironmentVariableStep.Arguments.optional(),
    stepKind: zod.literal('SetEnvironmentVariable'),
  }),
  zod.object({
    args: SetIOStep.Arguments.optional(),
    stepKind: zod.literal('SetIO'),
  }),
  zod.object({
    args: TeleopStep.Arguments.optional(),
    stepKind: zod.literal('Teleop'),
  }),
  zod.object({
    args: WaitStep.Arguments.optional(),
    stepKind: zod.literal('Wait'),
  }),
  zod.object({
    args: WaitForConfirmationStep.Arguments.optional(),
    stepKind: zod.literal('WaitForConfirmation'),
  }),
  zod.object({
    args: CNCRunProgramStepDatabase.Arguments.optional(),
    stepKind: zod.literal('CNCRunProgram'),
  }),
  zod.object({
    args: CNCMonitorMachineStepDatabase.Arguments.optional(),
    stepKind: zod.literal('CNCMonitorMachine'),
  }),
  zod.object({
    args: CNCOperateAutodoorStepDatabase.Arguments.optional(),
    stepKind: zod.literal('CNCOperateAutodoor'),
  }),
  zod.object({
    args: CNCOperateWorkholdingStepDatabase.Arguments.optional(),
    stepKind: zod.literal('CNCOperateWorkholding'),
  }),
  zod.object({
    args: DisableGripperStepDatabase.Arguments.optional(),
    stepKind: zod.literal('DisableGripper'),
  }),
  zod.object({
    args: EnableGripperStepDatabase.Arguments.optional(),
    stepKind: zod.literal('EnableGripper'),
  }),
  zod.object({
    args: MoveVerticalLiftStepDatabase.Arguments.optional(),
    stepKind: zod.literal('MoveVerticalLift'),
  }),
  zod.object({
    args: WeldStepDatabase.Arguments.optional(),
    stepKind: zod.literal('Weld'),
  }),
  zod.object({
    args: TriggerFaultStep.Arguments.optional(),
    stepKind: zod.literal('TriggerFault'),
  }),
  zod.object({
    args: WaypointStep.Arguments.optional(),
    stepKind: zod.literal('Waypoint'),
  }),
  zod.object({
    args: UpdateVisualizationStep.Arguments.optional(),
    stepKind: zod.literal('UpdateVisualization'),
  }),
]);

export type StepConfigurationByKind = zod.infer<typeof StepConfigurationByKind>;

// Cannot do pick on discriminated union.
// Trying to extract args from .map on union type leads to type confusion elsewhere. Just lump it for now.
export const StepConfigurationByKindArgs = zod.union([
  ActuateScrewdriverStepDatabase.Arguments,
  ActuateGripperStepDatabase.Arguments,
  ActuateVacuumStepDatabase.Arguments,
  AnticipatePayloadStep.Arguments,
  AntigravityStep.Arguments,
  AddOffsetStep.Arguments,
  ArcStep.Arguments,
  CaptureStep.Arguments,
  ClassifyStep.Arguments,
  CodeBlockStep.Arguments,
  CreateRecordingStep.Arguments,
  LocateStep.Arguments,
  HaasControlRegionStepDatabase.Arguments,
  HaasRunProgramStepDatabase.Arguments,
  ElseStep.Arguments,
  ElseIfStep.Arguments,
  IfStep.Arguments,
  LoopStep.Arguments,
  LoopControlStep.Arguments,
  MoveArmToTargetStep.Arguments,
  MoveArmToTargetStepV2.Arguments,
  NetworkRequestStep.Arguments,
  OperateEquipmentStep.Arguments,
  PressButtonStep.Arguments,
  PushModeStep.Arguments,
  RunInBackgroundStep.Arguments,
  RunSkillStep.Arguments,
  SetEnvironmentVariableStep.Arguments,
  SetIOStep.Arguments,
  TeleopStep.Arguments,
  WaitStep.Arguments,
  WaitForConfirmationStep.Arguments,
  WaypointStep.Arguments,
  UpdateVisualizationStep.Arguments,
  CNCRunProgramStepDatabase.Arguments,
  CNCMonitorMachineStepDatabase.Arguments,
  CNCOperateAutodoorStepDatabase.Arguments,
  CNCOperateWorkholdingStepDatabase.Arguments,
  DisableGripperStepDatabase.Arguments,
  EnableGripperStepDatabase.Arguments,
  MoveVerticalLiftStepDatabase.Arguments,
  WeldStepDatabase.Arguments,
  TriggerFaultStep.Arguments,
]);

export type StepConfigurationByKindArgs = zod.infer<
  typeof StepConfigurationByKindArgs
>;
